<template>
  <el-dialog v-model="isVisible" title="Výsledky vyhledávání" width="90%" :fullscreen="isFullscreen"
    :close-on-click-modal="true" :close-on-press-escape="true" class="search-results-dialog"
    @opened="handleDialogOpened">
    <!-- Search Input Container -->
    <div class="search-input-container">
      <el-input v-model="searchQuery" :placeholder="`Zadejte hledaný text (min. ${MIN_SEARCH_LENGTH} znaky)...`"
        clearable :prefix-icon="Search" size="large" @clear="handleClear" @keyup.enter="performSearch"
        ref="searchInputRef" />
    </div>

    <!-- Results Container -->
    <div v-loading="isLoadingSearch" class="search-results-container">

      <!-- Empty State: Query Too Short -->
      <EmptyState v-if="searchQuery.trim().length < MIN_SEARCH_LENGTH"
        :message="`Zadejte alespoň ${MIN_SEARCH_LENGTH} znaky pro vyhledávání`" />

      <!-- Empty State: No Results -->
      <EmptyState v-else-if="
        searchResults &&
        searchResults.contracts.length === 0 &&
  searchResults.insuranceCases.length === 0" :message="`Žádné výsledky pro '${searchQuery.trim()}'`" />

      <!-- Results with Tabs -->
      <el-tabs v-else-if="searchResults" v-model="activeTab" class="search-results-tabs">
        <el-tab-pane v-if="showContractsTab" name="contracts">
          <template #label>
            <span class="tabs-label">
              <el-icon>
                <Document />
              </el-icon>
              <span class="tabs-label-text">Smlouvy</span>
              <span class="count-badge">{{ searchResults?.contracts?.length || 0 }}</span>
            </span>
          </template>
          <ContractsList :filtered-contracts="searchResults?.contracts || []"
            :total-count="searchResults?.contracts?.length || 0" :is-loading="false"
            :table-columns="['product', 'partner']" :disable-navigation="selectionMode"
            @select="handleContractSelect" />
        </el-tab-pane>

        <el-tab-pane v-if="showInsuranceTab" name="insuranceCases">
          <template #label>
            <span class="tabs-label">
              <el-icon>
                <Money />
              </el-icon>
              <span class="tabs-label-text">Pojistné události</span>
              <span class="count-badge">{{ searchResults?.insuranceCases?.length || 0 }}</span>
            </span>
          </template>
          <InsuranceCasesList :filtered-insurance-cases="searchResults?.insuranceCases || []"
            :total-count="searchResults?.insuranceCases?.length || 0" :is-loading="false"
            :table-columns="['reportNumber', 'date', 'partner']" :disable-navigation="selectionMode"
            @select="handleInsuranceCaseSelect" />
        </el-tab-pane>
      </el-tabs>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, shallowRef, computed } from 'vue'
import { refDebounced, useWindowSize } from '@vueuse/core'
import { ElDialog, ElInput, ElTabs, ElTabPane, ElIcon } from 'element-plus'
import { Search, Document, Money } from '@element-plus/icons-vue'
import { useCustomerStore } from '@/stores/customer'
import { storeToRefs } from 'pinia'
import ContractsList from '@/components/contract/ContractsList.vue'
import InsuranceCasesList from '@/components/insurance/InsuranceCasesList.vue'
import EmptyState from '@/components/common/EmptyState.vue'
import type { Contract, InsuranceCase } from '@/types/customer'
import { SearchTarget } from '@/types/search'

interface Props {
  modelValue: boolean
  selectionMode?: boolean
  searchTarget?: SearchTarget | null
}

const props = withDefaults(defineProps<Props>(), {
  selectionMode: false,
  searchTarget: null,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  select: [item: { type: 'contract' | 'insurance-case'; data: unknown }]
}>()

const customerStore = useCustomerStore()
const { selectedClientId, isLoadingSearch, searchResults } = storeToRefs(customerStore)

// Responsive screen size detection for fullscreen mode
const { width: windowWidth } = useWindowSize()
const isFullscreen = computed(() => windowWidth.value < 768)

const MIN_SEARCH_LENGTH = 3
const searchInputRef = ref<InstanceType<typeof ElInput> | null>(null)
const activeTab = ref('contracts')
const showContractsTab = computed(
  () => !props.searchTarget || props.searchTarget === SearchTarget.contracts,
)
const showInsuranceTab = computed(
  () => !props.searchTarget || props.searchTarget === SearchTarget.insuranceCases,
)
const defaultTab = computed(() => (showContractsTab.value ? 'contracts' : 'insuranceCases'))

// Dialog visibility
const isVisible = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value)
  },
})

// Search query with debouncing
const searchQuery = shallowRef('')
const debouncedSearchQuery = refDebounced(searchQuery, 500)

// Watch for debounced changes and trigger search
watch(debouncedSearchQuery, async (newQuery) => {
  const trimmedQuery = newQuery.trim()

  if (trimmedQuery.length >= MIN_SEARCH_LENGTH) {
    await customerStore.loadSearch(selectedClientId.value, trimmedQuery, props.searchTarget)
  } else if (trimmedQuery.length === 0) {
    // Clear results when input is empty
    customerStore.searchResults = null
  }
})

// Manual search function (for Enter key)
const performSearch = async () => {
  const trimmedQuery = searchQuery.value.trim()
  if (trimmedQuery.length >= MIN_SEARCH_LENGTH) {
    await customerStore.loadSearch(selectedClientId.value, trimmedQuery, props.searchTarget)
  }
}

// Handle clear button
const handleClear = () => {
  customerStore.searchResults = null
}

// Auto-focus input when dialog opens
const handleDialogOpened = () => {
  nextTick(() => {
    searchInputRef.value?.focus()
  })
}

// Reset active tab when dialog opens
watch(isVisible, (isOpen) => {
  if (isOpen) {
    activeTab.value = defaultTab.value
  }
})

watch(
  () => props.searchTarget,
  () => {
    activeTab.value = defaultTab.value
  },
  { immediate: true },
)

// Handle selection events from child components
// In selection mode: emit select event to parent
// In navigation mode: navigate to detail page
const handleContractSelect = (contract: Contract) => {
  if (props.selectionMode) {
    emit('select', { type: 'contract', data: contract })
    nextTick(() => {
      isVisible.value = false
    })
  } else {
    nextTick(() => {
      isVisible.value = false
    })
  }
}

const handleInsuranceCaseSelect = (insuranceCase: InsuranceCase) => {
  if (props.selectionMode) {
    emit('select', { type: 'insurance-case', data: insuranceCase })
    nextTick(() => {
      isVisible.value = false
    })
  } else {
    nextTick(() => {
      isVisible.value = false
    })
  }
}
</script>

<style scoped>
.search-results-dialog {
  --el-dialog-padding-primary: 20px;
}

.search-input-container {
  margin-bottom: 20px;
}

.search-hint {
  margin-top: 8px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.search-results-container {
  min-height: 400px;
  max-height: 70vh;
  overflow-y: auto;
}

.search-results-tabs {
  margin-top: 0;
}

.search-results-tabs :deep(.el-tabs__content) {
  padding-top: 0px;
  }

  .tabs-label-text {
    padding-left: 5px;
    padding-right: 5px;
  }

  .tabs-label-text {
    font-size: 14px;
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .search-results-dialog {
    --el-dialog-width: 95%;
  }

  .search-results-container {
    max-height: calc(100vh - 120px);
  }
}
</style>

